import {
  CommandType,
  DatabaseReportInputModel,
  DocumentReportInputModel,
  ReportInputModel,
  ReportParameterInputModel,
  Visibility,
} from '@ail/reports-api'
import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useGetTenantUsersQuery, useGetTenantsQuery } from '../../../redux/api/customerApi'
import {
  isDatabaseReportInput,
  isDocumentReportInput,
  useGetReportsAdminQuery,
  usePostDatabaseReportAdminMutation,
  usePostDocumentReportAdminMutation,
  usePutDatabaseReportAdminMutation,
  usePutDocumentReportAdminMutation,
  useUploadDocumentReportAdminMutation,
} from '../../../redux/api/reportsApi'
import './ReportEdit.scss'
import { OracleDbType } from '../../../types/enums'
import ValidCompanies from './section/ValidCompanies'
import ReportParameters from './section/ReportParameters'
import AllowedUsersList from './section/allowedUsersList'
import { idToKEY, isGuid, isKEY } from '../../../helpers/Utils'
import { Loader } from '../../common/Loader/Loader'

const ParametersRegex = new RegExp(':\\w+', 'g')

export function ReportEdit() {
  const [editing, setEditing] = useState<ReportInputModel>()
  const [pendingFieldsJson, setPendingFieldsJson] = useState<string>()
  const [reportFile, setReportFile] = useState<File>()
  const [fieldsJson, setFieldsJson] = useState<string>('')
  const [isStandard, setIsStandard] = useState<boolean>(false)
  const params = useParams<{ id?: string }>()
  const history = useHistory()
  const reports = useGetReportsAdminQuery()
  const tenants = useGetTenantsQuery()

  const [postDatabaseReport, postDatabaseReportResult] = usePostDatabaseReportAdminMutation()
  const [putDatabaseReport, putDatabaseReportResult] = usePutDatabaseReportAdminMutation()
  const [postDocumentReport, postDocumentReportResult] = usePostDocumentReportAdminMutation()
  const [putDocumentReport, putDocumentReportResult] = usePutDocumentReportAdminMutation()
  const [uploadDocumentReport, uploadDocumentReportResult] = useUploadDocumentReportAdminMutation()

  const tenantUsers = useGetTenantUsersQuery(editing?.tenantId ?? skipToken)
  const reportId = params.id ?? ''
  const creatingNew = params.id === 'new'
  const loading =
    reports.isFetching ||
    tenantUsers.isFetching ||
    postDatabaseReportResult.isLoading ||
    putDatabaseReportResult.isLoading ||
    postDocumentReportResult.isLoading ||
    putDocumentReportResult.isLoading ||
    uploadDocumentReportResult.isLoading

  useEffect(() => {
    if (reportId === 'new') {
      const newReport: DatabaseReportInputModel = {
        fields: [],
        parameters: [],
        connectionString: '',
        commandType: CommandType.Text,
        commandText: '',
        allowedUsers: [],
        visibility: Visibility.Private,
        name: '',
        kind: 'database',
      }
      setEditing(newReport)
    } else {
      setEditing(reports.currentData?.find((r) => r.id === reportId))
    }
    setPendingFieldsJson(undefined)
  }, [reportId, reports.currentData])

  useEffect(() => {
    if (!editing || pendingFieldsJson || !isDatabaseReportInput(editing)) return
    setFieldsJson(JSON.stringify(editing.fields, null, '\t'))
  }, [editing, pendingFieldsJson])

  const sortedTenantUsers = useMemo(() => {
    return (
      tenantUsers.currentData?.toSorted((a, b) => {
        let aVal = !a.enabled ? 100 : 0
        let bVal = !b.enabled ? 100 : 0
        aVal += !a.permissions.includes('reports.view') ? 10 : 0
        bVal += !b.permissions.includes('reports.view') ? 10 : 0
        aVal += a.name.localeCompare(b.name)
        bVal += b.name.localeCompare(a.name)
        return aVal - bVal
      }) ?? []
    )
  }, [tenantUsers.currentData])

  const ownerNormalizedId = useMemo(() => editing?.owner?.replaceAll('$', '').toLowerCase(), [editing?.owner])

  const ownerInvalid = !!editing?.owner && !isGuid(editing.owner) && !isKEY(editing.owner)

  const allowedTenantUsers = useMemo(() => {
    return ownerNormalizedId?.length === 36
      ? sortedTenantUsers?.filter((u) => u.customers.some((c) => c.id === ownerNormalizedId))
      : sortedTenantUsers
  }, [ownerNormalizedId, sortedTenantUsers])

  const allowedUsers = useMemo(() => {
    return editing?.allowedUsers.length
      ? allowedTenantUsers?.filter((u) => editing.allowedUsers.includes(u.id))
      : allowedTenantUsers
  }, [allowedTenantUsers, editing?.allowedUsers])

  const handleReportTypeChange = (kind: 'document' | 'database', standard = false) => {
    if (!editing || (editing.kind === kind && standard === isStandard)) return
    switch (kind) {
      case 'document': {
        const updated: DocumentReportInputModel = {
          kind,
          description: '',
          fileName: '',
          mimeType: '',
          allowedUsers: [],
          visibility: Visibility.Private,
          name: '',
        }
        setEditing(updated)
        setIsStandard(false)
        break
      }
      case 'database': {
        const updated: DatabaseReportInputModel = {
          kind,
          tenantId: standard ? undefined : editing.tenantId,
          fields: [],
          parameters: [],
          connectionString: '',
          commandType: CommandType.Text,
          commandText: '',
          allowedUsers: [],
          visibility: Visibility.Private,
          name: '',
        }
        setEditing(updated)
        setIsStandard(standard)
        break
      }
    }
  }

  const handleAllowedUsersChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value.replace(/\s/g, '')
    const allowedUsers: string[] = value.split(',')

    setEditing((current) => {
      if (!current) return current
      return { ...current, allowedUsers }
    })
  }

  const handleCommandTextChange = (value: string, report: DatabaseReportInputModel) => {
    const match = value.match(ParametersRegex)
    const updated: DatabaseReportInputModel = { ...report, commandText: value }
    if (match?.length) {
      const parameters = match.map((p) => {
        const paramName = p.slice(1)
        const existingParameter = updated.parameters?.find((p) => p.name === paramName)
        return (
          existingParameter ??
          ({
            type: OracleDbType.NVarchar2,
            name: p.slice(1),
            value: undefined,
          } as ReportParameterInputModel)
        )
      })
      updated.parameters = parameters
    } else {
      updated.parameters = []
    }
    setEditing(updated)
  }

  const applyFieldsJson = () => {
    try {
      if (editing && isDatabaseReportInput(editing)) {
        const updated: DatabaseReportInputModel = { ...editing, fields: JSON.parse(fieldsJson) }
        setEditing(updated)
        setPendingFieldsJson(undefined)
      }
    } catch (error) {
      alert(error)
    }
  }

  const submit = async (e: React.MouseEvent) => {
    e.preventDefault()
    if (!editing) return
    if (ownerInvalid) return

    const owner = !editing.owner
      ? undefined
      : isGuid(editing.owner)
      ? idToKEY(editing.owner)
      : editing.owner.toUpperCase()

    const model = { ...editing, owner, allowedUsers: allowedUsers.map((u) => u.id) }
    if (isDocumentReportInput(model)) {
      const promise = creatingNew ? postDocumentReport({ model }) : putDocumentReport({ reportId, model })

      try {
        const result = await promise.unwrap()
        if (reportFile) {
          await uploadDocumentReport({
            reportId: result.id,
            file: { fileName: reportFile.name, data: reportFile },
          }).unwrap()
        }
        history.push('/ReportsManagement')
      } catch {
        alert('Document report saving failed!')
      }
    } else if (isDatabaseReportInput(model)) {
      const promise = creatingNew ? postDatabaseReport({ model }) : putDatabaseReport({ reportId, model })
      try {
        await promise.unwrap()
        history.push('/ReportsManagement')
      } catch {
        alert('Database report saving failed!')
      }
    }
  }

  return (
    <Loader blocking={loading} style={{ overflow: 'hidden' }}>
      {!!editing && (
        <div className={`workflow row`}>
          <div className={`ordersArea newFormArea col-6`}>
            <div className="ordersFormArea">
              <div className="box-title">
                <span>
                  <i className="icon-edit" /> Edit report
                </span>
              </div>
            </div>
            <div className="request-form-edit">
              <div className="formGroup">
                <label>Report Type</label>
                <div className="control-group required-highlight">
                  <div className="row">
                    <div className="col-4">
                      <label className="control control-radio">
                        Database
                        <input
                          type="radio"
                          name="reportType"
                          value="database"
                          disabled={!creatingNew}
                          onChange={() => handleReportTypeChange('database')}
                          checked={isDatabaseReportInput(editing) && !isStandard}
                        />
                        <div className="control_indicator" />
                      </label>
                    </div>
                    <div className="col-4">
                      <label className="control control-radio">
                        Database (Standard)
                        <input
                          type="radio"
                          name="reportType"
                          value="standard"
                          disabled={!creatingNew}
                          onChange={() => handleReportTypeChange('database', true)}
                          checked={isDatabaseReportInput(editing) && isStandard}
                        />
                        <div className="control_indicator" />
                      </label>
                    </div>
                    <div className="col-4">
                      <label className="control control-radio">
                        Document
                        <input
                          type="radio"
                          name="reportType"
                          value="document"
                          disabled={!creatingNew}
                          onChange={() => handleReportTypeChange('document')}
                          checked={isDocumentReportInput(editing)}
                        />
                        <div className="control_indicator" />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              {!isStandard && (
                <div className="formGroup">
                  <label>Select owner tenant ID</label>
                  <div className="selectArea">
                    <select
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          tenantId: e.target.value || undefined,
                          owner: undefined,
                          allowedUsers: [],
                        })
                      }
                      value={editing.tenantId ?? ''}
                      name="tenantId"
                    >
                      <option value="" key="default">
                        Select owner Tenant
                      </option>
                      {tenants.currentData?.map((t) => (
                        <option value={t.id} key={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                    <i className="icon-bottom" />
                  </div>
                </div>
              )}
              {!isStandard && (
                <div className="formGroup">
                  <label>Owner</label>
                  <input
                    name={`fieldnameveryunique${new Date().getTime()}`}
                    autoComplete="none"
                    list="autocompleteOff"
                    type="text"
                    value={editing.owner ?? ''}
                    onChange={(e) => setEditing({ ...editing, owner: e.target.value || undefined })}
                  />
                  {editing.owner}
                  {ownerInvalid && (
                    <div className="notFound">
                      <i className="icon icon-warning" /> <span>Owner field has invalid format</span>
                    </div>
                  )}
                </div>
              )}

              <div className="formGroup">
                <label>Report name</label>
                <input
                  name={`name`}
                  autoComplete="none"
                  list="autocompleteOff"
                  type="text"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                />
              </div>
              {!isStandard && (
                <div className="formGroup">
                  <label>Tenant ID</label>
                  <input type="text" value={editing.tenantId ?? ''} disabled />
                </div>
              )}
              {!isStandard && (
                <div className="formGroup">
                  <label>List of allowed user IDs separated by commas</label>
                  <textarea
                    value={editing.allowedUsers?.join(',') ?? ''}
                    onChange={(e) => handleAllowedUsersChange(e)}
                  />
                </div>
              )}
              {isDatabaseReportInput(editing) && (
                <div>
                  <div className="formGroup">
                    <label>Command text *</label>
                    <textarea
                      value={editing.commandText ?? ''}
                      name={`commandText`}
                      autoComplete="none"
                      onChange={(e) => handleCommandTextChange(e.target.value, editing)}
                    />
                  </div>
                  <div className="formGroup">
                    <label>Connection string *</label>
                    <textarea
                      value={editing.connectionString ?? ''}
                      name={`connectionString`}
                      autoComplete="none"
                      onChange={(e) =>
                        setEditing({ ...editing, connectionString: e.target.value } as DatabaseReportInputModel)
                      }
                    />
                  </div>
                  <div className="formGroup">
                    <label>Command type *</label>
                    <div className="selectArea">
                      <select
                        value={editing.commandType}
                        name="commandType"
                        onChange={(e) =>
                          setEditing({ ...editing, commandType: e.target.value } as DatabaseReportInputModel)
                        }
                      >
                        {Object.values(CommandType).map((type) => (
                          <option value={type} key={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                      <i className="icon-bottom" />
                    </div>
                  </div>
                  {!isStandard && (
                    <div className="formGroup">
                      <label>Shedule</label>
                      <input
                        type="text"
                        value={editing.schedule ?? ''}
                        name={`schedule`}
                        autoComplete="none"
                        list="autocompleteOff"
                        onChange={(e) =>
                          setEditing({ ...editing, schedule: e.target.value } as DatabaseReportInputModel)
                        }
                      />
                    </div>
                  )}

                  <div className="formGroup">
                    <label>Enter new form's json</label>
                    <textarea
                      className="form-area"
                      onChange={(e) => {
                        setPendingFieldsJson(e.target.value)
                        setFieldsJson(e.target.value)
                      }}
                      value={fieldsJson}
                    />
                    <div className="apply-btn">
                      {!!pendingFieldsJson && <button onClick={applyFieldsJson}>Apply JSON</button>}
                    </div>
                  </div>
                </div>
              )}
              {isDocumentReportInput(editing) && (
                <div className="formGroup">
                  <label>Upload report</label>
                  <input
                    type="file"
                    id="upload"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      setReportFile(e.target.files?.item(0) ?? undefined)
                      setEditing({
                        ...editing,
                        fileName: e.target.files?.item(0)?.name ?? '',
                        mimeType: e.target.files?.item(0)?.type ?? '',
                      } as DocumentReportInputModel)
                    }}
                  />
                  <div className="flex">
                    <label className="upload-button" htmlFor="upload">
                      Choose file
                    </label>
                    {editing.fileName}
                  </div>
                </div>
              )}
              {isDocumentReportInput(editing) && (
                <div className="formGroup">
                  <label>Document description *</label>
                  <input
                    type="text"
                    value={editing.description ?? ''}
                    name={`description`}
                    autoComplete="none"
                    list="autocompleteOff"
                    onChange={(e) =>
                      setEditing({ ...editing, description: e.target.value } as DocumentReportInputModel)
                    }
                  />
                </div>
              )}

              <div className="formGroup">
                <label>Visibility status</label>
                <div className="selectArea">
                  <select
                    name="reportStatus"
                    onChange={(e) => setEditing({ ...editing, visibility: e.target.value as Visibility })}
                    value={editing.visibility}
                  >
                    {Object.values(Visibility).map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                  <i className="icon-bottom" />
                </div>
              </div>
            </div>

            <div className="btnArea">
              <a onClick={() => history.push('/ReportsManagement')} className="btn btnClose">
                <i className="icon-close" /> CANCEL
              </a>
              <a onClick={submit} className="btn btnSave">
                <i className="icon-check" /> SUBMIT
              </a>
            </div>
          </div>
          <div className="workflowSteps col-5 reports">
            {!!editing.tenantId && (
              <>
                <div className="detailTitle">
                  <i className="icon-groups" />
                  <span>Owners</span>
                </div>
                <div className="ordersContent">
                  <div className="owners">
                    <i className="icon-groups" />
                    <div>{tenants.currentData?.find((t) => t.id === editing.tenantId)?.name}</div>
                  </div>
                </div>
              </>
            )}
            {!isStandard && (
              <>
                {editing.tenantId ? (
                  <AllowedUsersList
                    users={sortedTenantUsers}
                    allowedUsers={editing.allowedUsers ?? []}
                    filteredUsersByOwner={allowedTenantUsers}
                  />
                ) : (
                  <div className="allowed-users">
                    <div className="title">
                      <span>
                        <i className="icon icon-warning" />
                        Please select a tenant.
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
            {!!ownerNormalizedId && <ValidCompanies validCompany={ownerNormalizedId} />}
            {isDatabaseReportInput(editing) && !!editing.parameters.length && (
              <ReportParameters
                report={editing}
                onChange={(e) => setEditing({ ...editing, parameters: e } as DatabaseReportInputModel)}
              />
            )}
          </div>
        </div>
      )}
    </Loader>
  )
}
